import behaviourManagementConfigs from "@/configs/behaviourManagementConfigs.js"
import { resolveBehaviourTerminology } from "@/configs/behaviourTerminology.js"
import behaviourConstants from "@/constants/behaviourManagementConstants.js"
import behaviourCoreValueConstants from "@/constants/behaviourCoreValueConstants.js"
import {
  mapApiCoreValueToRow,
  buildCoreValuesListParams
} from "@/helpers/behaviourCoreValueApi.js"
import {
  buildCoreValueCreatePayload,
  buildCoreValueStatusUpdatePayload,
  buildCoreValueUpdatePayload
} from "@/helpers/behaviourCoreValueForm.js"
import behaviourPositiveBehaviourConstants from "@/constants/behaviourPositiveBehaviourConstants.js"
import {
  mapApiPositiveBehaviourToRow,
  buildPositiveBehavioursListParams
} from "@/helpers/behaviourPositiveBehaviourApi.js"
import {
  buildPositiveBehaviourCreatePayload,
  buildPositiveBehaviourStatusUpdatePayload,
  buildPositiveBehaviourUpdatePayload
} from "@/helpers/behaviourPositiveBehaviourForm.js"
import behaviourAutomatedPointsConstants from "@/constants/behaviourAutomatedPointsConstants.js"
import {
  buildAutomatedPointsUpsertPayload,
  mapApiAutomatedPointsToForm
} from "@/helpers/behaviourAutomatedPointsForm.js"
import behaviourCustomizedThemeConstants from "@/constants/behaviourCustomizedThemeConstants.js"
import {
  buildCustomizedThemeUpsertPayload,
  customizedThemeApiToTerminologyOverrides,
  mapApiCustomizedThemeToForm
} from "@/helpers/behaviourCustomizedTheme.js"
import {
  buildBehaviourCsvUploadFormData,
  getBehaviourCsvUploadDefinition,
  parseBehaviourCsvUploadResponse
} from "@/helpers/behaviourCsvUpload.js"
import { apiV8nocache } from "@/store/modules/axiousWithCache"

const state = {
  school: {
    data: null
  },
  /** Partial terminology from school API; merged with locale defaults in getter `behaviourTerminology`. */
  terminologyOverrides: null
}

const getters = {
  behaviourTerminology: (state, _getters, _rootState, rootGetters) => {
    const locale = rootGetters["authentication/getLocale"]
    return resolveBehaviourTerminology(locale, state.terminologyOverrides)
  },
  userAccess: (_state, _getters, _rootState, rootGetters) => {
    const activeSchoolId = rootGetters["schools/activeSchool"]?.id
    const featureEnabled =
      behaviourManagementConfigs.isBehaviourManagementEnabled(activeSchoolId)
    if (!featureEnabled) {
      return {
        behaviourModule: false,
        behaviourSettings: false
      }
    }
    const currentRole = rootGetters["authentication/getUserRole"]
    if (behaviourConstants.BEHAVIOUR_ADMIN_ROLES.includes(currentRole)) {
      return {
        behaviourModule: true,
        behaviourSettings: true
      }
    }
    return {
      behaviourModule: false,
      behaviourSettings: false
    }
  },
  hasBehaviourSettingsAccess: (_state, getters) => {
    return getters?.userAccess?.behaviourSettings || false
  },
  hasBehaviourModuleAccess: (_state, getters) => {
    return getters?.userAccess?.behaviourModule || false
  },
  behaviourModule: (_state, _getters, rootState) => {
    const activeSchoolModules = rootState?.schools?.activeSchoolModules || []
    return (
      activeSchoolModules.find((module) =>
        behaviourConstants.MODULE_ALIASES.includes(module?.name)
      ) || null
    )
  },
  isBehaviourModuleActive: (_state, getters) => {
    const behaviourModule = getters.behaviourModule
    return behaviourModule ? Boolean(behaviourModule?.pivot?.status) : false
  }
}

const mutations = {
  /**
   * @param {Record<string, string> | null} payload Flat camelCase terminology from API
   */
  SET_BEHAVIOUR_TERMINOLOGY_OVERRIDES(state, payload) {
    state.terminologyOverrides =
      payload && typeof payload === "object" ? { ...payload } : null
  }
}

const actions = {
  /**
   * Loads customized theme settings and applies EN terminology overrides when rename is on.
   * ES locale always uses defaults (no custom labels from API).
   */
  async fetchBehaviourTerminology(context) {
    return context.dispatch("fetchCustomizedThemeSettings")
  },

  /**
   * GET /api/v8/admin/pbis/customized-theme-settings
   * @returns {Promise<import("@/helpers/behaviourCustomizedTheme.js").CustomizedThemeSettings>}
   */
  async fetchCustomizedThemeSettings({ commit, rootGetters }) {
    const locale = rootGetters["authentication/getLocale"]
    const response = await apiV8nocache.get(
      behaviourCustomizedThemeConstants.CUSTOMIZED_THEME_API_PATH
    )
    const apiData = response?.data?.data ?? response?.data ?? {}
    const form = mapApiCustomizedThemeToForm(apiData, locale)
    const overrides = customizedThemeApiToTerminologyOverrides(apiData, locale)
    commit("SET_BEHAVIOUR_TERMINOLOGY_OVERRIDES", overrides)
    return form
  },

  /**
   * PUT /api/v8/admin/pbis/customized-theme-settings
   * @param {import("@/helpers/behaviourCustomizedTheme.js").CustomizedThemeSettings} form
   * @returns {Promise<import("@/helpers/behaviourCustomizedTheme.js").CustomizedThemeSettings>}
   */
  async upsertCustomizedThemeSettings({ commit, rootGetters }, form) {
    const locale = rootGetters["authentication/getLocale"]
    const payload = buildCustomizedThemeUpsertPayload(form, locale)
    const response = await apiV8nocache.put(
      behaviourCustomizedThemeConstants.CUSTOMIZED_THEME_API_PATH,
      payload
    )
    const apiData = response?.data?.data ?? response?.data ?? {}
    const savedForm = mapApiCustomizedThemeToForm(apiData, locale)
    const overrides = customizedThemeApiToTerminologyOverrides(apiData, locale)
    commit("SET_BEHAVIOUR_TERMINOLOGY_OVERRIDES", overrides)
    return savedForm
  },

  /**
   * GET /api/v8/admin/pbis/core-values
   * @param {{ page?: number, perPage?: number, search?: string, statusFilter?: string }} query
   * @returns {Promise<{ rows: Record<string, unknown>[], pagination: Record<string, unknown> }>}
   */
  async fetchCoreValuesList(_context, query = {}) {
    const params = buildCoreValuesListParams(query)
    const response = await apiV8nocache.get(
      behaviourCoreValueConstants.CORE_VALUES_API_PATH,
      { params }
    )
    const body = response?.data
    const rows = (body?.data || []).map(mapApiCoreValueToRow)
    return {
      rows,
      pagination: body?.pagination || {}
    }
  },

  /**
   * POST /api/v8/admin/pbis/core-values
   * @param {Record<string, unknown>} form Core value form state
   * @returns {Promise<{ id: number }>}
   */
  async createCoreValue(_context, form) {
    const payload = buildCoreValueCreatePayload(form)
    const response = await apiV8nocache.post(
      behaviourCoreValueConstants.CORE_VALUES_API_PATH,
      payload
    )
    const body = response?.data
    const id = Number(body?.id ?? body?.data?.id)
    if (!Number.isFinite(id)) {
      throw new Error("Invalid create core value response")
    }
    return { id }
  },

  /**
   * PUT /api/v8/admin/pbis/core-values/{id}
   * Blank response body is treated as success.
   * @param {{ id: number|string, form: Record<string, unknown>, existingRow?: Record<string, unknown> }} payload
   * @returns {Promise<{ id: number }>}
   */
  async updateCoreValue(_context, { id, form, existingRow = {} }) {
    const coreValueId = Number(id)
    if (!Number.isFinite(coreValueId)) {
      throw new Error("Invalid core value id")
    }
    const body = buildCoreValueUpdatePayload(form, existingRow)
    await apiV8nocache.put(
      `${behaviourCoreValueConstants.CORE_VALUES_API_PATH}/${coreValueId}`,
      body
    )
    return { id: coreValueId }
  },

  /**
   * PUT /api/v8/admin/pbis/core-values/{id} — enable/disable (status 0 or 1 only).
   * @param {{ id: number|string, existingRow: Record<string, unknown>, status: string }} payload
   * @returns {Promise<{ id: number }>}
   */
  async updateCoreValueStatus(_context, { id, existingRow, status }) {
    const coreValueId = Number(id)
    if (!Number.isFinite(coreValueId)) {
      throw new Error("Invalid core value id")
    }
    const body = buildCoreValueStatusUpdatePayload(existingRow, status)
    await apiV8nocache.put(
      `${behaviourCoreValueConstants.CORE_VALUES_API_PATH}/${coreValueId}`,
      body
    )
    return { id: coreValueId }
  },

  /**
   * DELETE /api/v8/admin/pbis/core-values/{id}
   * Optional `replacement_core_value_id` reassigns references before deletion.
   * Backend returns 204 No Content on success.
   * @param {{ id: number|string, replacementCoreValueId?: number|string|null }} payload
   * @returns {Promise<{ id: number }>}
   */
  async deleteCoreValue(_context, payload = {}) {
    const { id, replacementCoreValueId = null } = payload || {}
    const coreValueId = Number(id)
    if (!Number.isFinite(coreValueId)) {
      throw new Error("Invalid core value id")
    }

    const body = {}
    if (
      replacementCoreValueId != null &&
      String(replacementCoreValueId) !== ""
    ) {
      const replacementId = Number(replacementCoreValueId)
      if (Number.isFinite(replacementId) && replacementId !== coreValueId) {
        body.replacement_core_value_id = replacementId
      }
    }

    await apiV8nocache.delete(
      `${behaviourCoreValueConstants.CORE_VALUES_API_PATH}/${coreValueId}`,
      Object.keys(body).length ? { data: body } : undefined
    )
    return { id: coreValueId }
  },

  /**
   * GET /api/v8/admin/pbis/positive-behaviors
   * @param {{ page?: number, perPage?: number, search?: string, statusFilter?: string, coreValueId?: string|number|null }} query
   * @returns {Promise<{ rows: Record<string, unknown>[], pagination: Record<string, unknown> }>}
   */
  async fetchPositiveBehavioursList(_context, query = {}) {
    const params = buildPositiveBehavioursListParams(query)
    const response = await apiV8nocache.get(
      behaviourPositiveBehaviourConstants.POSITIVE_BEHAVIOURS_API_PATH,
      { params }
    )
    const body = response?.data
    const rows = (body?.data || []).map(mapApiPositiveBehaviourToRow)
    return {
      rows,
      pagination: body?.pagination || {}
    }
  },

  /**
   * POST /api/v8/admin/pbis/positive-behaviors
   * @param {Record<string, unknown>} form
   * @returns {Promise<{ id: number }>}
   */
  async createPositiveBehaviour(_context, form) {
    const payload = buildPositiveBehaviourCreatePayload(form)
    const response = await apiV8nocache.post(
      behaviourPositiveBehaviourConstants.POSITIVE_BEHAVIOURS_API_PATH,
      payload
    )
    const body = response?.data
    const id = Number(body?.id ?? body?.data?.id)
    if (!Number.isFinite(id)) {
      throw new Error("Invalid create positive behaviour response")
    }
    return { id }
  },

  /**
   * PUT /api/v8/admin/pbis/positive-behaviors/{id}
   * HTTP 204 empty body is treated as success.
   * @param {{ id: number|string, form: Record<string, unknown>, existingRow?: Record<string, unknown> }} payload
   * @returns {Promise<{ id: number }>}
   */
  async updatePositiveBehaviour(_context, { id, form, existingRow = {} }) {
    const positiveBehaviourId = Number(id)
    if (!Number.isFinite(positiveBehaviourId)) {
      throw new Error("Invalid positive behaviour id")
    }
    const body = buildPositiveBehaviourUpdatePayload(form, existingRow)
    await apiV8nocache.put(
      `${behaviourPositiveBehaviourConstants.POSITIVE_BEHAVIOURS_API_PATH}/${positiveBehaviourId}`,
      body
    )
    return { id: positiveBehaviourId }
  },

  /**
   * PUT /api/v8/admin/pbis/positive-behaviors/{id} — enable/disable (status 0 or 1 only).
   * @param {{ id: number|string, existingRow: Record<string, unknown>, status: string }} payload
   * @returns {Promise<{ id: number }>}
   */
  async updatePositiveBehaviourStatus(_context, { id, existingRow, status }) {
    const positiveBehaviourId = Number(id)
    if (!Number.isFinite(positiveBehaviourId)) {
      throw new Error("Invalid positive behaviour id")
    }
    const body = buildPositiveBehaviourStatusUpdatePayload(existingRow, status)
    await apiV8nocache.put(
      `${behaviourPositiveBehaviourConstants.POSITIVE_BEHAVIOURS_API_PATH}/${positiveBehaviourId}`,
      body
    )
    return { id: positiveBehaviourId }
  },

  /**
   * DELETE /api/v8/admin/pbis/positive-behaviors/{id}
   * @param {number|string} id
   * @returns {Promise<{ id: number }>}
   */
  async deletePositiveBehaviour(_context, id) {
    const positiveBehaviourId = Number(id)
    if (!Number.isFinite(positiveBehaviourId)) {
      throw new Error("Invalid positive behaviour id")
    }
    await apiV8nocache.delete(
      `${behaviourPositiveBehaviourConstants.POSITIVE_BEHAVIOURS_API_PATH}/${positiveBehaviourId}`
    )
    return { id: positiveBehaviourId }
  },

  /**
   * GET /api/v8/admin/pbis/automated-points-settings
   * @returns {Promise<Record<string, unknown>>}
   */
  async fetchAutomatedPointsSettings() {
    const response = await apiV8nocache.get(
      behaviourAutomatedPointsConstants.AUTOMATED_POINTS_API_PATH
    )
    const body = response?.data
    const settings = body?.data?.settings ?? body?.settings ?? []
    return mapApiAutomatedPointsToForm(settings)
  },

  /**
   * PUT /api/v8/admin/pbis/automated-points-settings
   * @param {Record<string, unknown>} form
   * @returns {Promise<Record<string, unknown>>}
   */
  async upsertAutomatedPointsSettings(_context, form) {
    const payload = buildAutomatedPointsUpsertPayload(form)
    const response = await apiV8nocache.put(
      behaviourAutomatedPointsConstants.AUTOMATED_POINTS_API_PATH,
      payload
    )
    const body = response?.data
    const settings = body?.data?.settings ?? body?.settings ?? []
    return mapApiAutomatedPointsToForm(settings)
  },

  /**
   * POST /api/v8/admin/pbis/positive-behaviors/upload
   * @param {{ uploadKind: string, file: File }} payload
   * @returns {Promise<import("@/helpers/behaviourCsvUpload.js").BehaviourCsvUploadResult>}
   */
  async uploadBehaviourCsv(_context, { uploadKind, file }) {
    const def = getBehaviourCsvUploadDefinition(uploadKind)
    if (!def?.uploadApiPath) {
      throw new Error("Invalid CSV upload kind")
    }
    if (!file) {
      throw new Error("CSV file is required")
    }

    const formData = buildBehaviourCsvUploadFormData(file)
    const response = await apiV8nocache.post(def.uploadApiPath, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })

    return parseBehaviourCsvUploadResponse(response?.data)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
